// ClipRadar website — pricing, roadmap, FAQ, footer. (The closing CTA lives in
// final-lantern.tsx — the Monument moment.)
import { useEffect, useState } from 'react';
import { CRWIcon, type IconName } from '../components/icons';
import { MetalBtn, MonoNote, SectionHead } from '../components/primitives';
import { RequestAccessModal } from '../components/request-access-modal';
import type { WaitlistTier } from '../components/use-waitlist-submit';
import { startCheckout, useSalesOpen, type PurchasePlan } from '../components/use-checkout';
import { track } from '../track';

// Neutral icon tile — the A1 chrome-neutral variant of IconTile: same glass
// plate, fg-2 stroke icon, no warm halo (marketing icons are neutral chrome now).
function NeutralTile({ d, size = 38 }: { d: IconName; size?: number }) {
  return (
    <span style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: 11, background: 'linear-gradient(160deg, rgba(255,255,255,0.09), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.12)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 8px 20px rgba(0,0,0,0.4)', color: 'var(--fg-2)' }}>
      <CRWIcon d={d} size={Math.round(size * 0.46)} />
    </span>
  );
}

interface PriceCard { id: WaitlistTier; name: string; price: string; unit: string; billing: string; who: string; hero?: boolean }

// Each card answers three questions in order: what does it cost, how is it
// billed, who is it for. The "who" line does the selling; the fine print
// closes the last doubt. No badges, no repeated caps labels.
const PRICE_CARDS: PriceCard[] = [
  { id: 'monthly', name: 'Monthly', price: '$29', unit: '/mo', billing: 'Billed monthly', who: 'For trying it on a real project.' },
  { id: 'founding', name: 'Founding', price: '$199', unit: 'once', billing: 'One payment — yours forever', who: 'For editors done with subscriptions. A year of updates included; the app is yours for good.', hero: true },
  { id: 'annual', name: 'Annual', price: '$249', unit: '/yr', billing: 'Billed yearly · about $21/mo', who: 'For the committed — two months on the house.' },
];

// Every plan is the same full product — the feature list repeats by design, led
// by the face/people wedge. Rendered INSIDE each card (no shared box). Kept a
// touch long on purpose: each line is a real differentiator, not filler.
const PRICE_FEATS: [string, boolean][] = [
  ['Face, speech and visual search — one local library', true],
  ['Name a face once — every appearance, every drive', false],
  ['Speech search in 110+ languages, to the exact word', false],
  ['Ultra Search built in, runs on your own GPU', false],
  ['Premiere Pro panel + the standalone desktop app', false],
  ['Results drag straight onto your timeline', false],
  ['Unlimited searches · terabyte-scale · 100% local', false],
];

function PriceFeatures({ hero, shared }: { hero?: boolean; shared?: boolean }) {
  return (
    <ul style={{ listStyle: 'none', margin: shared ? 0 : '18px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {PRICE_FEATS.map(([f, lead]) => (
        <li key={f} style={{ display: 'flex', gap: 9, fontSize: shared ? 13 : 12.5, lineHeight: 1.5, color: lead ? 'var(--fg-1)' : 'var(--fg-2)', fontWeight: lead ? 600 : 400 }}>
          <span style={{ color: hero ? 'var(--signal-400)' : 'var(--fg-3)', display: 'inline-flex', flex: 'none', paddingTop: 1 }}><CRWIcon d="check" size={13} sw={2.5} /></span>{f}
        </li>
      ))}
    </ul>
  );
}

function PriceCardView({ card, seatLabel, onPick, cta }: { card: PriceCard; seatLabel: string; onPick: () => void; cta: string }) {
  const body = (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, borderRadius: card.hero ? 17 : 18, background: card.hero ? 'rgba(6,6,8,0.98)' : undefined, backdropFilter: card.hero ? 'blur(20px)' : undefined, padding: card.hero ? '26px 24px 24px' : '24px 22px 22px', overflow: 'hidden' }}>
      {card.hero ? <span aria-hidden="true" style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, var(--signal-400), transparent)' }}></span> : null}
      {card.hero ? <span aria-hidden="true" style={{ position: 'absolute', top: -30, left: '50%', transform: 'translateX(-50%)', width: 300, height: 170, background: 'radial-gradient(ellipse, rgba(var(--light-rgb),0.16), transparent 70%)', pointerEvents: 'none' }}></span> : null}

      {/* header — tier name + (founding) live seat counter */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, minHeight: 24 }}>
        <MonoNote style={card.hero ? { color: 'var(--signal-300)' } : undefined}>{card.name}</MonoNote>
        {card.hero ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--signal-300)', background: 'rgba(var(--light-mid-rgb),0.1)', border: '1px solid rgba(var(--light-mid-rgb),0.3)', padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>
            <i className="crw-pulse" style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--signal-400)', display: 'inline-block', fontStyle: 'normal' }}></i>{seatLabel}
          </span>
        ) : null}
      </div>

      {/* price — display face, tabular, the card's loudest element */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '18px 0 0' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: card.hero ? 52 : 46, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1, color: 'var(--fg-1)', fontVariantNumeric: 'tabular-nums' }}>{card.price}</span>
        <span style={{ fontSize: 14, color: 'var(--fg-3)' }}>{card.unit}</span>
        {card.hero ? <span style={{ marginLeft: 4, fontSize: 15, color: 'var(--fg-4)', textDecoration: 'line-through' }}>$349</span> : null}
      </div>

      {/* billing cadence */}
      <div style={{ marginTop: 9, fontSize: 12, color: card.hero ? 'var(--signal-300)' : 'var(--fg-3)' }}>{card.billing}</div>

      {/* who this plan is for — the line that sells */}
      <p style={{ margin: '14px 0 0', fontSize: 13, lineHeight: 1.6, color: 'var(--fg-2)', textWrap: 'pretty' }}>{card.who}</p>

      {/* divider + features — INSIDE the card on desktop; on mobile the stacked
          cards would repeat the same seven lines three times, so the cards go
          compact and ONE shared list renders after the grid instead */}
      <div className="crw-price-feats" style={{ margin: '18px 0 0', borderTop: '1px solid var(--line-1)' }}></div>
      <div className="crw-price-feats">
        <PriceFeatures hero={card.hero} />
      </div>

      {/* CTA pinned to the bottom so all three align */}
      <div style={{ marginTop: 'auto', paddingTop: 22 }}>
        {card.hero ? (
          <MetalBtn onClick={onPick} style={{ width: '100%' }}>{cta}</MetalBtn>
        ) : (
          <button onClick={onPick} className="crw-pricebtn" style={{ width: '100%', boxSizing: 'border-box', cursor: 'pointer', padding: '12px 18px', borderRadius: 11, fontSize: 13.5, fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--fg-1)', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--line-2)', transition: 'background 160ms var(--ease-out), border-color 160ms var(--ease-out)' }}>
            {cta}
          </button>
        )}
      </div>
    </div>
  );

  if (card.hero) {
    return (
      <div className="crw-price-hero" style={{ position: 'relative', display: 'flex', flexDirection: 'column', borderRadius: 18, padding: 1, background: 'linear-gradient(170deg, rgba(var(--light-soft-rgb),0.6), rgba(255,255,255,0.08) 38%, rgba(var(--light-rgb),0.5) 88%)', boxShadow: '0 30px 80px rgba(0,0,0,0.55), 0 0 90px rgba(var(--light-rgb),0.16)' }}>
        {body}
      </div>
    );
  }
  return <div className="crw-glass" style={{ display: 'flex', flexDirection: 'column', borderRadius: 18 }}>{body}</div>;
}

export function Pricing() {
  const [seats, setSeats] = useState<{ total: number; left: number } | null>(null);
  const [modalTier, setModalTier] = useState<WaitlistTier | null>(null);
  const salesOpen = useSalesOpen();
  const [busy, setBusy] = useState<PurchasePlan | null>(null);

  useEffect(() => {
    let alive = true;
    fetch('/api/founding-seats')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d && typeof d.left === 'number' && typeof d.total === 'number') setSeats(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const seatLabel = seats ? `${seats.left} of ${seats.total} left` : 'Limited founding seats';

  // Coming BACK from Stripe restores this page from the back-forward cache with
  // React state intact — busy would still name the clicked plan and every buy
  // button would be dead until a manual refresh. pageshow with e.persisted marks
  // exactly that restore; clear the in-flight state so the cards work again.
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => { if (e.persisted) setBusy(null); };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  // Sales open → CTAs go straight to Stripe Checkout; a failed checkout falls
  // back to the waitlist modal so a visitor is never left with a dead button.
  const pick = async (card: PriceCard) => {
    if (!salesOpen) { setModalTier(card.id); return; }
    if (busy) return;
    const plan = card.id as PurchasePlan;
    track('buy_click', plan);
    setBusy(plan);
    const ok = await startCheckout(plan);
    if (!ok) { setBusy(null); setModalTier(card.id); }
  };

  const ctaFor = (card: PriceCard) => {
    if (!salesOpen) return 'Request early access';
    if (busy === card.id) return 'Opening checkout…';
    return card.hero ? `Buy Founding — ${card.price}` : `Buy ${card.name}`;
  };

  return (
    <section id="pricing" style={{ padding: '90px 24px', position: 'relative' }}>
      {/* one section-level radial glow (not three per-card halos) */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '50%', top: '14%', transform: 'translateX(-50%)', width: 1080, height: 700, background: 'radial-gradient(ellipse 52% 52% at 50% 42%, rgba(var(--light-rgb),0.10), transparent 70%), radial-gradient(ellipse 44% 48% at 60% 62%, rgba(var(--light-mid-rgb),0.07), transparent 70%)', pointerEvents: 'none' }}></div>
      <div style={{ maxWidth: 1040, margin: '0 auto', position: 'relative' }}>
        <div style={{ textAlign: 'center', marginBottom: 40, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}>
          <span className="crw-eyebrow" style={{ justifyContent: 'center' }}><i></i>Pricing</span>
          <h2 className="crw-h-grad" style={{ margin: '14px 0 0', fontFamily: 'var(--font-display)', fontSize: 'clamp(30px, 4.2vw, 46px)', fontWeight: 700, letterSpacing: 'var(--tracking-display)', lineHeight: 1.1, textWrap: 'balance' }}>
            One app. Subscribe, or <span style={{ WebkitTextFillColor: 'var(--fg-1)', color: 'var(--fg-1)' }}>own it forever.</span>
          </h2>
          <p style={{ margin: '14px auto 0', fontSize: 15, lineHeight: 1.6, color: 'var(--fg-2)', textWrap: 'pretty' }}>
            Every plan is the same full product — we just split the billing.
          </p>
          <p style={{ margin: '10px auto 0', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.08em', color: 'var(--fg-4)', textTransform: 'uppercase' }}>
            {salesOpen ? 'Instant delivery · license key by email · powered by Stripe' : 'Launching soon · invite-only · no card required'}
          </p>
        </div>

        <div className="crw-pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, alignItems: 'stretch' }}>
          {PRICE_CARDS.map((c) => (
            <PriceCardView key={c.id} card={c} seatLabel={seatLabel} cta={ctaFor(c)} onPick={() => void pick(c)} />
          ))}
        </div>

        {/* mobile only — the shared list the compact cards point to */}
        <div className="crw-price-feats-shared crw-glass" style={{ borderRadius: 16, padding: '20px 20px 22px', maxWidth: 420, margin: '14px auto 0' }}>
          <span className="cr-label" style={{ display: 'block', marginBottom: 14 }}>Every plan includes</span>
          <PriceFeatures shared />
        </div>
      </div>

      <RequestAccessModal tier={modalTier ?? 'general'} open={modalTier !== null} onClose={() => setModalTier(null)} />
    </section>
  );
}

/* ---------- ROADMAP — the flight queue ---------- */
export function Roadmap() {
  const items: { icon: IconName; t: string; d: string; status: string; live?: boolean }[] = [
    { icon: 'users', t: 'Team library sharing', d: 'One indexed library, every editor on the team searching it — still without a cloud.', status: 'NEXT UP', live: true },
    { icon: 'bot', t: 'Deeper agent editing', d: 'The MCP server ships today. Next: richer rough-cut direction, straight into your NLE.', status: 'IN THE LAB' },
    { icon: 'apple', t: 'macOS · Apple Silicon', d: 'The full release goes Mac — analysis running native on Apple Silicon.', status: 'QUEUED' },
    { icon: 'film', t: 'More NLEs', d: 'Resolve and Final Cut join Premiere. Same library, same search, your timeline.', status: 'QUEUED' },
  ];
  return (
    <section style={{ padding: '70px 24px', position: 'relative' }}>
      <div aria-hidden="true" style={{ position: 'absolute', left: '50%', top: '0%', transform: 'translateX(-50%)', width: 1000, height: 500, background: 'radial-gradient(ellipse, rgba(255,255,255,0.03), transparent 65%)', pointerEvents: 'none' }}></div>
      <div style={{ maxWidth: 1120, margin: '0 auto', position: 'relative' }}>
        <SectionHead center label="On the roadmap" title="What’s shipping next." sub="The beta queue, in the order it stands today — beta members vote it up or down every week." />
        <div className="crw-roadmap" style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          {/* trajectory line through the queue */}
          <span className="crw-roadline" aria-hidden="true" style={{ position: 'absolute', left: '2%', right: '2%', top: -14, height: 1, background: 'linear-gradient(90deg, rgba(255,255,255,0.22), rgba(255,255,255,0.08) 55%, transparent 98%)', opacity: 0.45 }}></span>
          {items.map((it, i) => (
            <div key={it.t} className="crw-glass crw-card-hover" style={{ position: 'relative', borderRadius: 16, padding: '20px 20px 18px', display: 'flex', flexDirection: 'column', gap: 10, overflow: 'hidden' }}>
              {/* node on the trajectory */}
              <span className="crw-roadline" aria-hidden="true" style={{ position: 'absolute', left: '50%', top: 0, transform: 'translate(-50%, -50%)', width: 7, height: 7, borderRadius: '50%', background: i === 0 ? 'var(--fg-2)' : 'var(--ink-600)', border: '1px solid rgba(255,255,255,0.25)', boxShadow: i === 0 ? '0 0 10px rgba(255,255,255,0.22)' : 'none' }}></span>
              {/* ghost queue numeral */}
              <b aria-hidden="true" style={{ position: 'absolute', right: 6, top: -14, fontFamily: 'var(--font-display)', fontSize: 84, fontWeight: 700, letterSpacing: '-0.05em', color: 'rgba(255,255,255,0.035)', pointerEvents: 'none', userSelect: 'none' }}>{i + 1}</b>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <NeutralTile d={it.icon} size={36} />
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-mono)', fontSize: 8.5, letterSpacing: '0.12em', color: it.live ? 'var(--fg-2)' : 'var(--fg-3)', background: it.live ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.04)', border: `1px solid ${it.live ? 'var(--line-3)' : 'var(--line-2)'}`, padding: '3px 8px', borderRadius: 999 }}>
                  {it.live ? <i className="crw-pulse" style={{ width: 4.5, height: 4.5, borderRadius: '50%', background: 'var(--fg-2)', display: 'inline-block', fontStyle: 'normal' }}></i> : null}
                  {it.status}
                </span>
              </div>
              <b style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg-1)', position: 'relative' }}>{it.t}</b>
              <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--fg-3)', position: 'relative' }}>{it.d}</p>
            </div>
          ))}
        </div>
        <p style={{ margin: '24px 0 0', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-3)', letterSpacing: '0.04em' }}>
          <span style={{ color: 'var(--fg-2)' }}>↑↓</span> Beta members vote on the order — every week.
        </p>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
export function FAQ() {
  const qs: [string, string][] = [
    ['Do I need a GPU?', 'Yes — ClipRadar requires an NVIDIA GPU (CUDA); it does not run without one. Everything happens locally on your GPU, which is exactly what makes indexing terabytes practical. Around 6 GB of VRAM is a comfortable baseline — more VRAM just means faster indexing.'],
    ['Is there a Mac version?', 'Not yet — the beta is Windows-first. macOS support, with native Apple Silicon acceleration, is coming in the full release. Join the Discord and you’ll be the first to know the moment it lands.'],
    ['What formats does it support?', 'If your machine can decode it, ClipRadar can index it — H.264/265, ProRes, DNxHR, RAW workflows via proxies, plus audio-only files like WAV and MP3.'],
    ['Does anything upload? Does it need internet?', 'Nothing uploads, ever. Analysis, the index, and search all run locally. Internet is only needed to download the app and model updates.'],
    ['How big can my library be?', 'There’s no cap. ClipRadar is built for terabyte-scale libraries spread across internal drives, external RAIDs and NAS volumes — including drives that are sometimes offline.'],
    ['How much does ClipRadar cost?', 'Start free: the trial gives you 40 searches on your own footage, no credit card. After that it’s one app, billed your way — $29/mo, $249/yr, or a one-time $199 Founding Edition (it becomes $349 after launch). Every plan unlocks the same full product; we split the billing, not the features.'],
    ['How private is my footage?', 'As private as your disk. Footage, transcripts, faces and the search index never leave your machine. There is no account requirement to search, no telemetry on your media, no cloud processing.'],
    ['Which languages does speech search cover?', '110+ languages, searchable in the original language — Japanese, Arabic, Swiss German dialects, and more. You can also search across languages in one query scope.'],
    ['What is Ultra Search?', 'A local LLM that sits in front of your index. Type a whole sentence — “Nadia announcing the budget at the town hall” — and it splits the query into people, spoken words and visuals, runs every search mode at once, and fuses the results into one ranked list. Like everything else, it runs on your GPU: no cloud, no per-query cost.'],
  ];
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" style={{ padding: '70px 24px 90px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <SectionHead center label="Questions" title="The things editors actually ask." />
        <div>
          {qs.map(([q, a], i) => {
            const on = open === i;
            return (
              <div key={i} style={{ borderBottom: '1px solid var(--line-1)' }}>
                <button onClick={() => setOpen(on ? -1 : i)} className="crw-faq-q" aria-expanded={on}>
                  <b style={{ flex: 1, fontSize: 15.5, fontWeight: 600, color: on ? 'var(--fg-1)' : 'var(--fg-2)', transition: 'color 150ms var(--ease-out)' }}>{q}</b>
                  <span style={{ color: on ? 'var(--fg-1)' : 'var(--fg-3)', display: 'inline-flex', transition: 'transform 250ms var(--ease-out), color 150ms var(--ease-out)', transform: on ? 'rotate(45deg)' : 'none' }}><CRWIcon d="plus" size={16} /></span>
                </button>
                <div style={{ display: 'grid', gridTemplateRows: on ? '1fr' : '0fr', transition: 'grid-template-rows 300ms var(--ease-out)' }}>
                  <div style={{ overflow: 'hidden', minHeight: 0 }}>
                    <p style={{ margin: '0 0 20px', padding: '0 4px', fontSize: 14, lineHeight: 1.7, color: 'var(--fg-3)', maxWidth: '60ch' }}>{a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

